import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';

const BatchCard = ({ batch, onViewDetails }) => {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: 'warning', label: 'Pending' },
      COMPLETED: { variant: 'success', label: 'Completed' },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  const statusCounts = batch.statusCounts || {
    sent: 0,
    submitted: 0,
    verified: 0,
    expired: 0,
  };

  const totalVendors = batch._count?.requests || batch.requests?.length || 0;
  const verifiedCount = statusCounts.verified || 0;
  const progressPercentage = totalVendors > 0 ? (verifiedCount / totalVendors) * 100 : 0;

  return (
    <Card className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-300">
      <CardContent className="p-6">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-slate-900">Batch #{batch.id}</h3>
              {getStatusBadge(batch.status)}
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Icon name="Calendar" size={14} />
                <span>Sent {formatDate(batch.sent_at)}</span>
              </span>
              {batch.completed_at && (
                <span className="flex items-center gap-1.5">
                  <Icon name="CheckCircle" size={14} />
                  <span>Completed {formatDate(batch.completed_at)}</span>
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 ml-4">
            <Button onClick={onViewDetails} variant="outline" size="sm">
              View Details
            </Button>
          </div>
        </div>

        <div className="h-px bg-slate-200 my-4" />

        {/* Vendor Count Breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Total Vendors</p>
            <p className="text-lg font-semibold text-slate-900">{totalVendors}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Sent</p>
            <p className="text-lg font-semibold text-slate-900">{statusCounts.sent}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Submitted</p>
            <p className="text-lg font-semibold text-amber-600">{statusCounts.submitted}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Verified</p>
            <p className="text-lg font-semibold text-emerald-600">{statusCounts.verified}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-600">Progress</span>
            <span className="text-xs font-medium text-slate-600">
              {verifiedCount} / {totalVendors}
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Expired Requests Warning */}
        {statusCounts.expired > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
            <Icon name="AlertTriangle" size={16} />
            <span>{statusCounts.expired} request(s) expired</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BatchCard;
